// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Checkbox,
  MenuItem,
  Divider,
  FormLabel,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
// ** Icon Imports
import Icon from 'components/icon';

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";
import { deleteAttachmentAPI, getAttachments, getServiceRequests, uploadAttachment } from "screens/DispatchBoard/logic/DispatchBoardLogic";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GetCustomer } from "screens/Customers/logic/GetCustomer";
import dayjs from "dayjs";
import { SavePayment } from "../logic/PaymentLogic";
import { useServiceRequestPermission } from "screens/Requests/permissions/RequestPermissions";

import Stripecheckout from "images/Stripecheckout.jpg";
import { CreateCheckoutSession } from "components/StripeConnect/logic/stripe-api";

const allowedTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/webp",
  "image/svg+xml",
];
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const RecordPaymentAdvance = ({ open, handleClose, fetchPayments }) => {
  //#region Permissions
  const { ServiceRequestPermissionProps } = useServiceRequestPermission();
  const { hasServiceGetPermission } = ServiceRequestPermissionProps; 
  //#endregion Permissions

  const [workOrders, setWorkOrders] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [selectedType, setSelectedType] = useState(""); // "WorkOrder" or "Assessment"
  const [selectedRequestId, setSelectedRequestId] = useState("");
  const [customerData, setCustomerData] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Card");
  const [paymentDate, setPaymentDate] = useState(dayjs()); // default to today
  const [amount, setAmount] = useState("");
  const [paidBy, setPaidBy] = useState("");
  const [note, setNote] = useState("");
  const [attachments, setAttachments] = useState([]);      // For user-picked File objects (local)
  const [uploadedAttachments, setUploadedAttachments] = useState([]); // For loaded from server, if you want to display them
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const handlePayment = async (paymentID) => {
    setIsProcessingPayment(true);

    try {
      const sessionResponse = await CreateCheckoutSession(paymentID);
      if (!sessionResponse?.result?.sessionId) {
        throw new Error("Failed to create session");
      }
      const sessionId = sessionResponse.result.sessionId;
      const CheckoutUrl = sessionResponse.result.url;
      window.location.href = CheckoutUrl; // Redirect to Stripe Checkout
    } catch (err) {
      console.error("Payment Error:", err.message);
      alert("Payment failed. Please try again."); // Show user feedback
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const theme = useTheme();

  const navigate = useNavigate();

  useEffect(() => {
    if(!hasServiceGetPermission) return;

    const fetch = async () => {
      try {
        const svcReqs = await getServiceRequests(navigate);
        setWorkOrders(svcReqs.filter(sr => sr.entityType === "WorkOrder"));
        setAssessments(svcReqs.filter(sr => sr.entityType === "Assessment"));
      } catch (e) {
        toast.error("Unable to load jobs — please check your login.", {
          position: "top-right",
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    };
    if(hasServiceGetPermission){
      fetch();
    }
  }, [hasServiceGetPermission]);

  useEffect(() => {
    // Find selected item from workOrders or assessments
    let selectedItem = null;
    if (selectedType === "WorkOrder") {
      selectedItem = workOrders.find(w => w.requestID === selectedRequestId);
    } else if (selectedType === "Assessment") {
      selectedItem = assessments.find(a => a.requestID === selectedRequestId);
    }
  
    if (selectedItem && selectedItem.customerID) {
      setLoadingCustomer(true);
      (async () => {
        try {
          const res = await GetCustomer(selectedItem.customerID);
 
          if (res && res.length > 0) {
           
            setCustomerData(res[0]);
          } else {
            setCustomerData(null);
          }
        } catch {
          setCustomerData(null);
        } finally {
          setLoadingCustomer(false);
        }
      })();
    } else {
      setCustomerData(null);
    }
  }, [selectedRequestId, selectedType, workOrders, assessments]);  

  const resetForm = () => {
    setPaymentMethod("");
    setAmount("");
    setPaymentDate(dayjs());
    setPaidBy("");
    setNote("");
    setSelectedType("");
    setSelectedRequestId("");
    setCustomerData(null);
    setAttachments([]);
    setUploadedAttachments([]);
    setErrors({});
    // Reset any other state you use in this dialog
  };

  const paymentTypes = [
    { label: "Work Order", value: "WorkOrder" },
    { label: "Assessment", value: "Assessment" }
  ];
  const requestIdOptions =
  selectedType === "WorkOrder"
    ? workOrders.map(w => ({
        label: `WRO-${w.requestID}`,
        value: w.requestID
      }))
    : selectedType === "Assessment"
      ? assessments.map(a => ({
          label: `AST-${a.requestID}`,
          value: a.requestID
        }))
      : [];
      
       const formatNumberWithCommas = (value) => {
  if (!value) return '';
  const parts = value.toString().replace(/,/g, '').split('.');
  parts[0] = parseFloat(parts[0]).toLocaleString('en-US');
  return parts.join('.');
};
const removeCommas = (value) => value.replace(/,/g, '');

const handleAmountChange = (e) => {
  const value = e.target.value;

  // remove commas first
  const rawValue = removeCommas(value);

  // allow only numbers and optional decimal
  if (/^\d*\.?\d*$/.test(rawValue)) {
    setAmount(rawValue); // store clean number (no commas)
    setErrors((prev) => ({ ...prev, amount: undefined }));
  }
};


  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = [];
    let fileError = "";

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        fileError = "Only image files are allowed (jpg, png, gif, bmp, webp, svg).";
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        fileError = "Each file must be less than 2 MB.";
        break;
      }
      validFiles.push(file);
    }

    if (fileError) {
      toast.error(fileError);
      return;
    }

    setAttachments((prev) => [...prev, ...validFiles]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Validate required fields (you can enhance as needed)
    const newErrors = {};
    if (!selectedType) newErrors.selectedType = "Payment for is required.";
    if (!selectedRequestId) newErrors.selectedRequestId = "ID is required.";
    if (!amount || Number(amount) <= 0) newErrors.amount = "Amount is required.";
    if (!paymentDate) newErrors.paymentDate = "Payment date is required.";
    if (!paymentMethod) newErrors.paymentMethod = "Payment method is required.";
    if (!paidBy) newErrors.paidBy = "Paid By is required.";
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;
    const paymentStatus = (['Card', 'BankTransfer', 'ACH','ApplePay','GooglePay', 'Other'].includes(paymentMethod))? 'Pending' : 'Paid'
  
    const payload = {
      paymentID: 0,
      paymentType: "Deposit",
      requestID: selectedRequestId,
      invoiceID: 0,
      notes: note,
      paymentDate: paymentDate ? paymentDate.format("YYYY-MM-DD") : null,
      amount: Number(amount),
      paymentMethod,
      paymentStatus: paymentStatus, // Set default or use actual status
      paidBy
    };
// console.log(payload)
    setLoading(true);

    try {
      const result = await SavePayment(payload, navigate);
      if (result && !result.isError) {
        // console.log(result)
        // toast.success("Payment logged successfully!");
        fetchPayments();
        if(!['Card', 'BankTransfer', 'ACH','ApplePay','GooglePay', 'Other'].includes(paymentMethod)){
          toast.success(result?.message || "Payment saved successfully.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        }                
        const paymentID = result.result?.paymentID;
        if(['Card', 'BankTransfer', 'ACH','ApplePay','GooglePay', 'Other'].includes(paymentMethod)){
          handlePayment(paymentID);          
        }
        if (attachments.length > 0 && paymentID) {
          for (const file of attachments) {
            try {
              await uploadAttachment(paymentID, file, "Payment");
            } catch (error) {
              console.log(error)
            }
          }
          // toast.success("All attachments uploaded.");
        }

        resetForm();
        handleClose(); // Or reset form as needed
      } else {
        toast.error(result?.message || "Failed to save payment.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      }
    } catch (err) {
      toast.error("An error occurred while logging payment.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    } finally {
      setLoading(false);
    }
  };  

  return (    
    <>
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={open}
      onClose={()=>{
        handleClose();
        resetForm();
      }}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Take a Payment - Advance
        </Typography>
        {/* <Typography variant="body1" color="text.secondary">
          PAY - 001
        </Typography> */}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Typography variant="h4">Advance Payment Information</Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider
              sx={{
                color: "text.disabled",
                "& .MuiDivider-wrapper": { px: 6 },
              }}
            />
          </Grid>
{/* WorkOrder or Assessment */}
          <Grid item xs={12} md={5}>
            <CustomAutocomplete
                options={paymentTypes}
                getOptionLabel={(option) => option.label}
                value={paymentTypes.find(opt => opt.value === selectedType) || null}
                onChange={(_, newValue) => {
                  setSelectedType(newValue ? newValue.value : "");
                  setSelectedRequestId("");
                  setErrors(prev => ({ ...prev, selectedType: undefined, selectedRequestId: undefined }));
                }}
                renderInput={(params) => (
                  <CustomTextField
                    fullWidth
                    placeholder="Select"
                    label="Payment For"
                    error={Boolean(errors.selectedType)}
                    helperText={errors.selectedType}
                    {...params}
                  />
                )}
              />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomAutocomplete
                options={requestIdOptions}
                getOptionLabel={(option) => option.label}
                value={requestIdOptions.find(opt => opt.value === selectedRequestId) || null}
                onChange={(_, newValue) => {
                  setSelectedRequestId(newValue ? newValue.value : "");
                  setErrors(prev => ({ ...prev, selectedRequestId: undefined }));
                }}
                renderInput={(params) => (
                  <CustomTextField
                    fullWidth
                    placeholder="ID"
                    label="ID"
                    error={Boolean(errors.selectedRequestId)}
                    helperText={errors.selectedRequestId}
                    {...params}
                  />
                )}
                disabled={!selectedType}
              />
          </Grid>

{/* Workorder complete */}

          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Customer Name"
              placeholder="Customer Name"
              value={
                customerData
                  ? customerData.customerType === "Individual"
                    ? `${customerData.firstName || ""} ${customerData.lastName || ""}`
                    : customerData.companyName || ""
                  : ""
              }
              InputProps={{ readOnly: true }}
              disabled={loadingCustomer}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Email"
              placeholder="Email"
              value={customerData ? customerData.email || "" : ""}
              InputProps={{ readOnly: true }}
              disabled={loadingCustomer}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Phone"
              placeholder="Phone"
              value={
                customerData && customerData.phoneNumbers && customerData.phoneNumbers.length > 0
                  ? customerData.phoneNumbers[0].phoneNo
                  : ""
              }
              InputProps={{ readOnly: true }}
              disabled={loadingCustomer}
            />
          </Grid>
          <Grid item xs={12} mt={4}>
            <Typography variant="h4">Payment Information</Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider
              sx={{
                color: "text.disabled",
                "& .MuiDivider-wrapper": { px: 6 },
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Amount"
              placeholder="Enter amount"
              value={formatNumberWithCommas(amount)}
              onChange={handleAmountChange}
              inputProps={{ inputMode: "decimal" }}
              error={Boolean(errors.amount)}
              helperText={errors.amount}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormLabel
              sx={{
                fontWeight: 400,
                fontSize: "13px",
                marginBottom: "4px",
                color: "text.primary",
                height: "100%",
              }}
            >
              Payment Date
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ width: "100%" }}
                value={paymentDate}
                maxDate={dayjs()} // disables future dates
                onChange={value => {
                  setPaymentDate(value);
                  setErrors(prev => ({ ...prev, paymentDate: undefined }));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: Boolean(errors.paymentDate),
                    helperText: errors.paymentDate
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
           <Grid item xs={12} md={5}>
                  <CustomAutocomplete
                    options={[
                      { value: 'ACH', label: 'ACH' },
                      { value: 'ApplePay', label: 'Apple Pay' },
                      { value: 'BankTransfer', label: 'Bank Transfer' },
                      { value: 'Card', label: 'Card' },
                      { value: 'Cash', label: 'Cash' },
                      { value: 'Check', label: 'Check' },
                      { value: 'GooglePay', label: 'Google Pay' },
                      { value: 'Other', label: 'Other' }
                    ]}
                    value={paymentMethod ? { value: paymentMethod, label: paymentMethod } : null}
                    onChange={(event, newValue) => {
                      setPaymentMethod(newValue?.value || '');
                      setErrors(prev => ({ ...prev, paymentMethod: undefined }));
                    }}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Payment Method"
                        error={Boolean(errors.paymentMethod)}
                        helperText={errors.paymentMethod}
                        placeholder="Select Payment Method"
                      />
                    )}
                    sx={{
                      '& .MuiInputBase-root': {
                        py: 0.5, 
                        fontSize: '0.875rem',
                        minHeight: '40px'
                      },
                      '& .MuiAutocomplete-listbox': {
                        py: 0,
                        '& .MuiAutocomplete-option': {
                          py: 0.5,
                          fontSize: '0.875rem',
                          minHeight: '32px'
                        }
                      }
                    }}
                    PaperProps={{
                      sx: {
                        maxHeight: 200
                      }
                    }}
                  />
                </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Paid By"
              placeholder="Enter name"
              value={paidBy}
              onChange={e => {
                setPaidBy(e.target.value);
                setErrors(prev => ({ ...prev, paidBy: undefined }));
              }}
              error={Boolean(errors.paidBy)}
              helperText={errors.paidBy}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <CustomTextField
              fullWidth
              label="Note"
              placeholder="Enter a Note"
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormLabel
              sx={{
                fontWeight: 400,
                fontSize: "13px",
                marginBottom: "4px",
                color: "text.primary",
                height: "100%",
              }}
            >
              Attachments
            </FormLabel>
            <Tooltip
              title={
                attachments.length
                  ? (
                    <Box>
                      {attachments.map((file, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pb: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={file.name}
                          >
                            {file.name}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{
                              p: "2px",
                              color: "#d32f2f",
                              '&:hover': { backgroundColor: '#fdecea' }
                            }}
                            onClick={(e) => {
                              // Prevent tooltip close on click
                              e.stopPropagation();
                              setAttachments(prev => prev.filter((_, i) => i !== idx));
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )
                  : "No files selected"
              }
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#fff",
                    color: "#111",
                    boxShadow: 3,
                    fontSize: 13,
                    borderRadius: 1,
                    p: 1,
                  }
                },
                arrow: {
                  sx: {
                    color: "#fff"
                  }
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 4,
                  py: 2,
                  gap: 2,
                  border: '1px solid rgba(47, 43, 61, 0.2)',
                  borderRadius: 1,
                  cursor: 'pointer'
                }}
                onClick={() => document.getElementById("attachment-input").click()}
              >
                <input
                  id="attachment-input"
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  multiple
                />
                <Icon icon='tabler:cloud-upload' />
                <Typography>
                  {attachments.length === 0 ? "Drop files to attach or browse" : `${attachments.length} file(s) selected`}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
          zIndex: 1,
        }}
        className="dialog-actions-dense"
      >
        <Box>
          <Button variant="contained" color="info" onClick={()=>{
            handleClose();
            resetForm();
          }}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
        <Box>
          <Button variant="contained" color="main" onClick={handleSubmit} disabled={loading}>
          {['Card', 'BankTransfer', 'ACH','ApplePay','GooglePay', 'Other'].includes(paymentMethod) ? 'Pay Now' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    {/* Stripe Checkout Redirect Dialog */}
    <Dialog open={isProcessingPayment} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Redirect to Stripe Checkout
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src={Stripecheckout} alt="Stripe Checkout" style={{ maxWidth: '100%', height: 'auto' }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body1" color="text.secondary">
              Redirecting to secure payment...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RecordPaymentAdvance;
